// Runtime migration/sanitizer for the locked ACTIVE building registry.
// Old files remain in source backup, but retired buildings/resources never
// appear in the live save, UI or simulation state.

import { useGame, EM2C_RESOURCES, EM2C_RESOURCE_KEYS } from "../em2c-store";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";

export const RETIRED_RESOURCE_KEYS = Object.freeze([
  "sorted_ore",
  "components",
  "building_modules",
]);

export const ACTIVE_OBJECTIVES = Object.freeze([
  { id: "camera", label: "Move the camera (WASD, drag with right-mouse)", auto: false },
  { id: "solar_power_station", label: "Build a Solar Power Station", type: "solar_station" },
  { id: "battery_storage", label: "Build a Battery Storage Facility", type: "battery_storage" },
  { id: "water_extractor", label: "Build a Regolith Water Extractor on a Hydrated Mineral Zone", type: "water_extractor" },
  { id: "water_purifier", label: "Build a Water Purification Plant", type: "water_purifier" },
  { id: "oxygen_gen", label: "Build an Oxygen Generator", type: "oxygen_gen" },
  { id: "habitat", label: "Build a Standard Habitat Module", type: "habitat_module" },
  { id: "greenhouse", label: "Build a Greenhouse", type: "greenhouse" },
  { id: "general_storage", label: "Build a General Storage Module", type: "small_storage" },
  { id: "relay", label: "Extend command coverage with a Relay Tower", type: "relay_tower" },
  { id: "survive", label: "Survive 5 Sols with positive life-support", sols: 5 },
]);

let installed = false;
let baseLoad = null;
let baseReset = null;

function dropRetiredResources(record = {}) {
  const next = { ...record };
  for (const key of RETIRED_RESOURCE_KEYS) delete next[key];
  return next;
}

export function sanitizeActiveRegistryState() {
  const state = useGame.getState();
  const buildings = (state.buildings || []).filter((building) => !!EM2C_BUILDINGS[building.type]);
  const liveIds = new Set(buildings.map((building) => building.id));

  // Clear occupancy left behind by a retired building from an old manual save.
  if (state.grid?.values) {
    for (const hex of state.grid.values()) {
      if (hex.occupied && hex.buildingId && !liveIds.has(hex.buildingId)) {
        hex.occupied = false;
        hex.buildingId = null;
      }
    }
  }

  const previousObjectives = new Map((state.objectives || []).map((objective) => [objective.id, objective]));
  const objectives = ACTIVE_OBJECTIVES.map((objective) => ({
    ...objective,
    done: Boolean(previousObjectives.get(objective.id)?.done),
  }));

  useGame.setState({
    resources: dropRetiredResources(state.resources),
    netRates: dropRetiredResources(state.netRates),
    buildings,
    objectives,
    selectedId: liveIds.has(state.selectedId) ? state.selectedId : (buildings[0]?.id || null),
  });
}

export function installActiveRegistryIntegration() {
  if (installed) return;
  installed = true;

  for (const key of RETIRED_RESOURCE_KEYS) delete EM2C_RESOURCES[key];
  for (let i = EM2C_RESOURCE_KEYS.length - 1; i >= 0; i--) {
    if (RETIRED_RESOURCE_KEYS.includes(EM2C_RESOURCE_KEYS[i])) EM2C_RESOURCE_KEYS.splice(i, 1);
  }

  const state = useGame.getState();
  baseLoad = state.load;
  baseReset = state.reset;
  useGame.setState({
    load: (...args) => {
      baseLoad?.(...args);
      sanitizeActiveRegistryState();
    },
    reset: (...args) => {
      baseReset?.(...args);
      sanitizeActiveRegistryState();
    },
  });
  sanitizeActiveRegistryState();
}
